import { create } from 'zustand';
import mapboxgl from 'mapbox-gl';
import { DEFAULT_POINT_SETTINGS } from '../constants/pointSettings';
import { PointSettings, LayerConfig, DataPoint } from '../../../types/metadata';
import { saveLayerState, saveLayerStateImmediate, loadLayerState } from './layerPersistence';

interface MapStore {
  map: mapboxgl.Map | null;
  viewport: {
    latitude: number;
    longitude: number;
    zoom: number;
  };

  // --- Layer State (Normalized) ---
  layerIds: string[]; // ['csv-1', 'csv-2'] - This controls the Z-index/Sidebar order
  layersConfig: Record<string, LayerConfig>; // Metadata for UI
  layersData: Record<string, DataPoint[][]>; // The actual heavy data arrays
  h3GridArea: DataPoint[] | null; // H3 grid area for debugging spatial queries
  h3DebugCells: string[]; // H3 resolution 2 cells used in last data request
  heatmapRanges: Record<string, { min: number; max: number }>; // Current visible heatmap value ranges per layer
  showDebugHexagons: boolean; // Toggle for debug hexagon overlay
  
  // --- Timestamp State ---
  lastSettingChange: number | null; // Epoch ms when a layer setting was last changed

  // --- Overlay State ---
  overlayOpen: boolean;
  overlayType: 'centers' | 'pcp' | 'ml' | null;
  overlayData: any;
  overlayCounter: number;
  catchmentArea: { type: 'polygon'; coordinates: number[][] } | { type: 'circle'; center: [number, number]; radiusMiles: number } | null;

  // --- Actions ---
  addLayer: (config: LayerConfig, data: DataPoint[][]) => void;
  updateLayerData: (id: string, data: DataPoint[][]) => void;
  updateLayerConfig: (id: string, updates: Partial<LayerConfig>) => void;
  updatePointSettings: (id: string, updates: Partial<PointSettings>) => void;
  getResolvedSettings: (id: string) => PointSettings;
  reorderLayers: (newOrder: string[]) => void;
  removeLayer: (layerId: string) => void;
  setH3GridArea: (gridArea: DataPoint[] | null) => void;
  setH3DebugCells: (cells: string[]) => void;
  setHeatmapRange: (layerId: string, min: number, max: number) => void;
  toggleDebugHexagons: () => void;
  openOverlay: (type: 'centers' | 'pcp' | 'ml', data: any) => void;
  closeOverlay: () => void;
  setCatchmentArea: (area: MapStore['catchmentArea']) => void;

  setMap: (map: mapboxgl.Map | null) => void;
  setViewport: (viewport: Partial<MapStore['viewport']>) => void;
  flyTo: (longitude: number, latitude: number, zoom?: number) => void;
}

const VIEWPORT_STORAGE_KEY = 'map-viewport';

const getInitialViewport = () => {
  try {
    const stored = localStorage.getItem(VIEWPORT_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load viewport from localStorage:', error);
  }
  return {
    latitude: 28.116865,
    longitude: -83.554533,
    zoom: 6.56,
  };
};

export const useMapStore = create<MapStore>((set, get) => ({
  map: null,
  viewport: getInitialViewport(),
  layerIds: [],
  layersConfig: {},
  layersData: {},
  h3GridArea: null,
  h3DebugCells: [],
  heatmapRanges: {},
  showDebugHexagons: false,
  lastSettingChange: null,
  overlayOpen: false,
  overlayType: null,
  overlayData: null,
  overlayCounter: 0,
  catchmentArea: null,
  setMap: (map) => set({ map }),
  setViewport: (viewport) =>
    set((state) => {
      const newViewport = { ...state.viewport, ...viewport };
      try {
        localStorage.setItem(VIEWPORT_STORAGE_KEY, JSON.stringify(newViewport));
      } catch (error) {
        console.error('Failed to save viewport to localStorage:', error);
      }
      return { viewport: newViewport };
    }),
  setH3DebugCells: (cells) => set({ h3DebugCells: cells }),
  setHeatmapRange: (layerId, min, max) =>
    set((state) => ({
      heatmapRanges: { ...state.heatmapRanges, [layerId]: { min, max } },
    })),
  toggleDebugHexagons: () => set((state) => ({ showDebugHexagons: !state.showDebugHexagons })),
  openOverlay: (type, data) => set((state) => ({ overlayOpen: true, overlayType: type, overlayData: data, overlayCounter: (state.overlayCounter || 0) + 1 })),
  closeOverlay: () => set({ overlayOpen: false, overlayType: null, overlayData: null, catchmentArea: null }),
  setCatchmentArea: (area) => set({ catchmentArea: area }),
  addLayer: (config, data) =>
    set((state) => {
      // Check if layer already exists
      const layerExists = state.layerIds.includes(config.id);
      
      if (layerExists) {
        console.log(`[MapStore] ⚠️ Layer already exists, use updateLayerData() instead: ${config.name} (${config.id})`);
      } else {
        console.log(`[MapStore] ✅ Adding new layer: ${config.name} (${config.id})`);
      }
      
      const newLayerIds = layerExists ? state.layerIds : [...state.layerIds, config.id];
      const newLayersConfig = { ...state.layersConfig, [config.id]: config };
      
      // Persist layer state immediately on add
      saveLayerStateImmediate(newLayerIds, newLayersConfig);
      
      return {
        layerIds: newLayerIds,
        layersConfig: newLayersConfig,
        layersData: { ...state.layersData, [config.id]: data },
        lastSettingChange: Date.now(),
      };
    }),
  updateLayerData: (id: string, data: DataPoint[][]) =>
    set((state) => {
      if (!state.layerIds.includes(id)) {
        console.warn(`[MapStore] ⚠️ Cannot update data for non-existent layer: ${id}`);
        return state;
      }
      return {
        layersData: { ...state.layersData, [id]: data },
      };
    }),
  reorderLayers: (newOrder) => set((state) => {
    saveLayerState(newOrder, state.layersConfig);
    return { layerIds: [...newOrder], lastSettingChange: Date.now() };
  }),
  updateLayerConfig: (id, newConfig) => set((state) => {
    const newLayersConfig = {
      ...state.layersConfig,
      [id]: { ...state.layersConfig[id], ...newConfig }
    };
    saveLayerState(state.layerIds, newLayersConfig);
    return { layersConfig: newLayersConfig, lastSettingChange: Date.now() };
  }),
  updatePointSettings: (id, newSettings) => set((state) => {
    const newLayersConfig = {
      ...state.layersConfig,
      [id]: {
        ...state.layersConfig[id],
        pointSettings: {
          ...(state.layersConfig[id].pointSettings || {} as PointSettings),
          ...newSettings
        } as PointSettings
      }
    };
    saveLayerState(state.layerIds, newLayersConfig);
    return { layersConfig: newLayersConfig, lastSettingChange: Date.now() };
  }),
  getResolvedSettings: (id) => {
    const config = get().layersConfig[id]?.pointSettings;
    return {...DEFAULT_POINT_SETTINGS, ...config}
  },
  removeLayer: (layerId) =>
    set((state) => {
      const { [layerId]: removedConfig, ...restConfig } = state.layersConfig;
      const { [layerId]: removedData, ...restData } = state.layersData;
      const newLayerIds = state.layerIds.filter((id) => id !== layerId);
      
      // Persist immediately on remove
      saveLayerStateImmediate(newLayerIds, restConfig);
      
      return {
        layerIds: newLayerIds,
        layersConfig: restConfig,
        layersData: restData,
        lastSettingChange: Date.now(),
      };
    }),
  setH3GridArea: (gridArea) => set({ h3GridArea: gridArea }),
  flyTo: (longitude, latitude, zoom = 14) => {
    const { map } = get();
    if (map) {
      map.flyTo({
        center: [longitude, latitude],
        zoom,
        essential: true,
      });
    }
    set((state) => {
      const newViewport = { ...state.viewport, latitude, longitude, zoom };
      try {
        localStorage.setItem(VIEWPORT_STORAGE_KEY, JSON.stringify(newViewport));
      } catch (error) {
        console.error('Failed to save viewport to localStorage:', error);
      }
      return { viewport: newViewport };
    });
  },
}));
